import type { FeatureDefinition } from "@growthbook/growthbook";

export const TH_LANDING_EXPERIMENT_KEY = "AYMEN-TH_Landing_Variant_Test-ON-531";
export const TH_LANDING_ASSIGNMENT_STORAGE_KEY = "thLandingAssignment";
export const VARIANT_SLUGS = {
  control: null,
  variant_a: "variant-a-mascot-human",
  variant_b: "variant-b-text-phone",
} as const;

export type LandingVariant = keyof typeof VARIANT_SLUGS;
/** Uses the anonymous ID so signup cannot move either an enrolled visitor or a holdout. */
export interface LandingAssignment {
  visitorId: string;
  bucketVersion: number;
  variant: LandingVariant | "holdout";
}

export function isLandingVariant(value: unknown): value is LandingVariant {
  return value === "control" || value === "variant_a" || value === "variant_b";
}

/**
 * Browser data may be stale or malformed. Validate it before restoring membership
 * so an unusable record cannot determine the visitor's cohort or analytics labels.
 */
export function isLandingAssignment(
  value: unknown
): value is LandingAssignment {
  if (!value || typeof value !== "object") {
    return false;
  }

  const hasValidVisitorId =
    "visitorId" in value &&
    typeof value.visitorId === "string" &&
    !!value.visitorId;
  if (!hasValidVisitorId) {
    return false;
  }

  const hasValidBucketVersion =
    "bucketVersion" in value && Number.isInteger(value.bucketVersion);
  if (!hasValidBucketVersion) {
    return false;
  }

  const hasValidCohort =
    "variant" in value &&
    (value.variant === "holdout" || isLandingVariant(value.variant));

  return hasValidCohort;
}

/**
 * Returns the rule supported by cohort persistence, or undefined if unsupported.
 * This selects a usable rule, not a validation verdict for all GrowthBook configurations.
 * GrowthBook's default result can mean either a holdout or an exclusion.
 * Active, untargeted enrollment keeps inactive visits and targeting failures
 * from being saved as persistent holdouts. Bucketing must use the SDK's id,
 * the identity this implementation uses for assignment and exposure.
 */
export function getSupportedLandingExperimentRule(
  feature: FeatureDefinition<unknown> | undefined
) {
  if (feature?.rules?.length !== 1) {
    return undefined;
  }

  const rule = feature.rules[0];
  if (rule.key !== TH_LANDING_EXPERIMENT_KEY) {
    return undefined;
  }

  const hasSupportedVariations =
    rule.variations && rule.variations.every(isLandingVariant);
  if (!hasSupportedVariations) {
    return undefined;
  }

  const hasValidCoverage =
    Number.isFinite(rule.coverage) && rule.coverage > 0 && rule.coverage <= 1;
  const usesSupportedIdentity =
    !rule.hashAttribute || rule.hashAttribute === "id";
  const hasTargeting =
    rule.condition || rule.filters || rule.parentConditions || rule.namespace;

  if (!hasValidCoverage || !usesSupportedIdentity || hasTargeting) {
    return undefined;
  }

  return rule;
}
